import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { 
  User, Mail, Phone, Building, MapPin, 
  Camera, Save, X, AlertCircle, Menu, Lock, Eye, EyeOff, CheckCircle, Circle, Shield,
  Network,
  Server,
  FileText,
  Key,
  Clock,
  Truck,
  Link,
  Unlink,
  RefreshCw,
  ExternalLink,
  Activity,
  Zap
} from 'lucide-react';
import { updateUserProfile } from '../store/slices/authSlice';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SideNavigation from '../components/SideNavigation';
import useScreenSize from '../hooks/useScreenSize';
import { fetchNotifications } from '../store/slices/notificationSlice';
import { selectOrganizationAddresses } from '../store/slices/authSlice';
import NotificationSettings from '../components/NotificationSettings';
import { ediApi, samsaraApi } from '../utils/api';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const PasswordSection = ({ isEditing, formData, setFormData, validations }) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { isMobile } = useScreenSize();

  // Password requirement status helper
  const getRequirementStatus = (key) => {
    if (!formData.newPassword) return 'neutral';
    return validations[key] ? 'valid' : 'invalid';
  };

  return (
    <div className={`${
      isMobile ? 'px-4 py-4' : 'px-6 py-6'
    }`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`font-medium text-gray-900 ${
          isMobile ? 'text-base' : 'text-lg'
        }`}>Password Settings</h3>
        <Lock className={`text-gray-400 ${
          isMobile ? 'h-4 w-4' : 'h-5 w-5'
        }`} />
      </div>

      {isEditing ? (
        <div className={`${
          isMobile ? 'space-y-4' : 'space-y-6'
        }`}>
          {/* Current Password */}
          <div className="relative">
            <label className={`block font-medium text-gray-700 ${
              isMobile ? 'text-sm mb-2' : 'text-sm'
            }`}>
              Current Password
              <span className="text-red-500 ml-1">*</span>
              <span className={`text-gray-500 ml-2 ${
                isMobile ? 'block text-xs mt-1 ml-0' : 'text-sm'
              }`}>(Required to change password)</span>
            </label>
            <div className={`relative rounded-md shadow-sm ${
              isMobile ? '' : 'mt-1'
            }`}>
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={formData.currentPassword || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  currentPassword: e.target.value
                }))}
                className={`block w-full pr-10 border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  isMobile ? 'py-3 text-base' : 'py-2'
                } ${
                  formData.newPassword && !formData.currentPassword ? 'border-red-300' : ''
                }`}
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showCurrentPassword ? (
                  <EyeOff className={`text-gray-400 ${
                    isMobile ? 'h-4 w-4' : 'h-5 w-5'
                  }`} />
                ) : (
                  <Eye className={`text-gray-400 ${
                    isMobile ? 'h-4 w-4' : 'h-5 w-5'
                  }`} />
                )}
              </button>
            </div>
            {formData.newPassword && !formData.currentPassword && (
              <p className={`mt-2 text-red-600 ${
                isMobile ? 'text-sm' : 'text-sm'
              }`}>
                Current password is required when setting a new password
              </p>
            )}
          </div>

          {/* New Password */}
          <div className="relative">
            <label className={`block font-medium text-gray-700 ${
              isMobile ? 'text-sm mb-2' : 'text-sm'
            }`}>
              New Password
            </label>
            <div className={`relative rounded-md shadow-sm ${
              isMobile ? '' : 'mt-1'
            }`}>
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={formData.newPassword || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  newPassword: e.target.value
                }))}
                className={`block w-full pr-10 border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  isMobile ? 'py-3 text-base' : 'py-2'
                }`}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showNewPassword ? (
                  <EyeOff className={`text-gray-400 ${
                    isMobile ? 'h-4 w-4' : 'h-5 w-5'
                  }`} />
                ) : (
                  <Eye className={`text-gray-400 ${
                    isMobile ? 'h-4 w-4' : 'h-5 w-5'
                  }`} />
                )}
              </button>
            </div>
          </div>

          {/* Confirm New Password */}
          <div className="relative">
            <label className={`block font-medium text-gray-700 ${
              isMobile ? 'text-sm mb-2' : 'text-sm'
            }`}>
              Confirm New Password
            </label>
            <div className={`relative rounded-md shadow-sm ${
              isMobile ? '' : 'mt-1'
            }`}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  confirmPassword: e.target.value
                }))}
                className={`block w-full pr-10 border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  isMobile ? 'py-3 text-base' : 'py-2'
                }`}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showConfirmPassword ? (
                  <EyeOff className={`text-gray-400 ${
                    isMobile ? 'h-4 w-4' : 'h-5 w-5'
                  }`} />
                ) : (
                  <Eye className={`text-gray-400 ${
                    isMobile ? 'h-4 w-4' : 'h-5 w-5'
                  }`} />
                )}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className={`bg-gray-50 rounded-lg border border-gray-200 ${
            isMobile ? 'p-3' : 'p-4'
          }`}>
            <h4 className={`font-medium text-gray-700 mb-4 flex items-center gap-2 ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}>
              <Shield className={`text-blue-500 ${
                isMobile ? 'w-4 h-4' : 'w-4 h-4'
              }`} />
              Password Requirements
            </h4>
            <ul className={`${
              isMobile ? 'space-y-2' : 'space-y-3'
            }`}>
              {[
                { key: 'length', text: 'At least 12 characters' },
                { key: 'uppercase', text: 'One uppercase letter (A-Z)' },
                { key: 'lowercase', text: 'One lowercase letter (a-z)' },
                { key: 'number', text: 'One number (0-9)' },
                { key: 'special', text: 'One special character (!@#$%^&*(),.?":{}|<>)' },
                { key: 'noCommon', text: 'Not a common password' }
              ].map(({ key, text }) => {
                const status = getRequirementStatus(key);
                return (
                  <li
                    key={key}
                    className={`flex items-center gap-3 ${
                      isMobile ? 'text-xs' : 'text-sm'
                    } ${
                      status === 'valid' ? 'text-green-600' : 
                      status === 'invalid' ? 'text-red-600' : 
                      'text-gray-500'
                    }`}
                  >
                    {status === 'valid' ? (
                      <CheckCircle className={`text-green-500 ${
                        isMobile ? 'w-4 h-4' : 'w-5 h-5'
                      }`} />
                    ) : status === 'invalid' ? (
                      <AlertCircle className={`text-red-500 ${
                        isMobile ? 'w-4 h-4' : 'w-5 h-5'
                      }`} />
                    ) : (
                      <Circle className={`text-gray-400 ${
                        isMobile ? 'w-4 h-4' : 'w-5 h-5'
                      }`} />
                    )}
                    <span className={`${
                      isMobile && key === 'special' ? 'break-all' : ''
                    }`}>{text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : (
        <p className={`text-gray-500 ${
          isMobile ? 'text-sm' : 'text-sm'
        }`}>
          Click "Edit Profile" to update your password
        </p>
      )}
    </div>
  );
};

const EdiSection = ({ 
  isEditing, 
  formData: parentFormData, 
  setFormData: setParentFormData,
  onSave  // Add this prop to receive the save handler from parent
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [ediErrors, setEdiErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Create separate EDI form data
  const [ediFormData, setEdiFormData] = useState({
    name: '',
    transportMethod: '',
    documentFormat: '',
    transportUrl: '',
    transportPort: '',
    username: '',
    password: '',
    tradingPartnerId: '',
    tradingPartnerQualifier: '',
    incomingFilePattern: '',
    outgoingFileNaming: '',
    autoProcessIncoming: true,
    acknowledgmentRequired: true,
    acknowledgmentTimeoutMinutes: 60,
    retryCount: 3,
    retryIntervalMinutes: 15
  });

  // Load existing configuration when component mounts or editing mode changes
  useEffect(() => {
    if (isEditing) {
      fetchExistingConfig();
    }
  }, [isEditing]);
  
  // Fetch existing configuration from backend
  const fetchExistingConfig = async () => {
    setLoading(true);
    try {
      // Attempt to fetch user's existing EDI configuration
      const { data } = await ediApi.getConfigurations();
      
      // If we have a configuration, populate the form
      if (data && data.configurations && data.configurations.length > 0) {
        const config = data.configurations[0]; // Use the first config or provide UI to select
        
        setEdiFormData({
          ediConfigId: config.id,
          name: config.name || '',
          transportMethod: config.transportMethod || '',
          documentFormat: config.documentFormat || '',
          transportUrl: config.transportUrl || '',
          transportPort: config.transportPort || '',
          username: config.username || '',
          // Don't set password as it's likely not returned from API for security
          // password: '',
          tradingPartnerId: config.tradingPartnerId || '',
          tradingPartnerQualifier: config.tradingPartnerQualifier || '',
          incomingFilePattern: config.incomingFilePattern || '',
          outgoingFileNaming: config.outgoingFileNaming || '',
          autoProcessIncoming: config.autoProcessIncoming ?? true,
          acknowledgmentRequired: config.acknowledgmentRequired ?? true,
          acknowledgmentTimeoutMinutes: config.acknowledgmentTimeoutMinutes ?? 60,
          retryCount: config.retryCount ?? 3,
          retryIntervalMinutes: config.retryIntervalMinutes ?? 15
        });
        
        toast.success('Loaded existing EDI configuration');
      } else {
        toast.info('No existing EDI configuration found. Create a new one.');
      }
    } catch (error) {
      console.error('Error fetching EDI configuration:', error);
      toast.error('Failed to load existing EDI configuration');
    } finally {
      setLoading(false);
    }
  };

  // Validation helper
  const validateEdiConfig = () => {
    const errors = {};
    
    // Required fields based on db model
    const requiredFields = {
      name: 'Configuration Name',
      transportMethod: 'Transport Method',
      documentFormat: 'Document Format',
      tradingPartnerId: 'Trading Partner ID',
      tradingPartnerQualifier: 'Trading Partner Qualifier'
    };

    // Check required fields
    Object.entries(requiredFields).forEach(([field, label]) => {
      if (!ediFormData[field]) {
        errors[field] = `${label} is required`;
      }
    });

    // Transport-specific validations
    if (ediFormData.transportMethod) {
      switch (ediFormData.transportMethod) {
        case 'ftp':
        case 'sftp':
        case 'http':
          if (!ediFormData.transportUrl?.trim()) {
            errors.transportUrl = 'Transport URL is required for this method';
          }
          if (!ediFormData.transportPort) {
            errors.transportPort = 'Port number is required for this method';
          }
          if (!ediFormData.username?.trim()) {
            errors.username = 'Username is required for this method';
          }
          if (!ediFormData.password?.trim() && !ediFormData.ediConfigId) {
            errors.password = 'Password is required for this method';
          }
          break;
        case 'as2':
          if (!ediFormData.transportUrl?.trim()) {
            errors.transportUrl = 'AS2 endpoint URL is required';
          }
          break;
      }
    }

    setEdiErrors(errors);
    
    // Log validation status to help debug
    if (Object.keys(errors).length > 0) {
      console.log('Validation failed with errors:', errors);
      console.log('Current form data:', ediFormData);
    }
    
    return Object.keys(errors).length === 0;
  };

  // Handle connection test
  const handleTestConnection = async () => {
    if (!ediFormData.ediConfigId) {
      toast.warning('Please save the configuration first');
      return;
    }
    
    setTestingConnection(true);
    try {
      const { data } = await ediApi.testConnection(ediFormData.ediConfigId);
      toast.success('Connection test successful!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to test connection');
    } finally {
      setTestingConnection(false);
    }
  };

  // Handle config save
  const handleSaveConfig = async () => {
    if (!validateEdiConfig()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingConfig(true);
    try {
      const configData = {
        name: ediFormData.name,
        transportMethod: ediFormData.transportMethod,
        documentFormat: ediFormData.documentFormat,
        transportUrl: ediFormData.transportUrl,
        transportPort: ediFormData.transportPort,
        username: ediFormData.username,
        password: ediFormData.password,
        tradingPartnerId: ediFormData.tradingPartnerId,
        tradingPartnerQualifier: ediFormData.tradingPartnerQualifier,
        incomingFilePattern: ediFormData.incomingFilePattern,
        outgoingFileNaming: ediFormData.outgoingFileNaming,
        autoProcessIncoming: ediFormData.autoProcessIncoming ?? true,
        acknowledgmentRequired: ediFormData.acknowledgmentRequired ?? true,
        acknowledgmentTimeoutMinutes: ediFormData.acknowledgmentTimeoutMinutes ?? 60,
        retryCount: ediFormData.retryCount ?? 3,
        retryIntervalMinutes: ediFormData.retryIntervalMinutes ?? 15
      };

      console.log('Sending EDI config data:', configData);

      const { data } = ediFormData.ediConfigId
        ? await ediApi.updateConfig(ediFormData.ediConfigId, configData)
        : await ediApi.createConfig(configData);

      setEdiFormData(prev => ({
        ...prev,
        ediConfigId: data.config.id
      }));

      toast.success('EDI configuration saved successfully');
    } catch (error) {
      console.error('Error saving EDI config:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save EDI configuration';
      toast.error(errorMessage);
    } finally {
      setSavingConfig(false);
    }
  };

  // Update the onChange handlers in your form fields to use ediFormData
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setEdiFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (ediErrors[name]) {
      setEdiErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle number input changes separately to ensure proper type conversion
  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    const numValue = value === '' ? '' : parseInt(value, 10);
    
    setEdiFormData(prev => ({
      ...prev,
      [name]: numValue
    }));
    
    // Clear error when user starts typing
    if (ediErrors[name]) {
      setEdiErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    
    setEdiFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Make this available to the parent component through the onSave prop
  useEffect(() => {
    if (onSave && typeof onSave === 'function') {
      onSave(handleSaveConfig);
    }
  }, [onSave, ediFormData]); // Re-register if ediFormData changes

  const { isMobile } = useScreenSize();
  
  return (
    <div className={`${
      isMobile ? 'px-4 py-4' : 'px-6 py-6'
    }`}>
      <div className={`mb-4 ${
        isMobile ? 'space-y-3' : 'flex items-center justify-between'
      }`}>
        <div>
          <h3 className={`font-medium text-gray-900 ${
            isMobile ? 'text-base' : 'text-lg'
          }`}>EDI Configuration</h3>
          <p className={`mt-1 text-gray-500 ${
            isMobile ? 'text-xs' : 'text-sm'
          }`}>
            Configure your EDI integration settings
          </p>
        </div>
        <div className={`flex items-center ${
          isMobile ? 'justify-between' : 'space-x-3'
        }`}>
          {isEditing && (
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testingConnection || !ediFormData.ediConfigId}
              className={`inline-flex items-center border border-gray-300 shadow-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isMobile ? 'px-3 py-2 text-sm' : 'px-3 py-1.5 text-sm'
              } ${
                testingConnection
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {testingConnection ? (
                <>
                  <span className="animate-spin mr-2">⟳</span>
                  Testing...
                </>
              ) : (
                <>
                  <Network className={`mr-1.5 ${
                    isMobile ? 'w-4 h-4' : 'w-4 h-4'
                  }`} />
                  {isMobile ? 'Test' : 'Test Connection'}
                </>
              )}
            </button>
          )}
          <Network className={`text-gray-400 ${
            isMobile ? 'h-4 w-4' : 'h-5 w-5'
          }`} />
        </div>
      </div>

      {loading ? (
        <div className={`flex justify-center items-center ${
          isMobile ? 'py-8' : 'py-10'
        }`}>
          <div className="animate-spin mr-3">⟳</div>
          <span className={`${
            isMobile ? 'text-sm' : ''
          }`}>Loading EDI configuration...</span>
        </div>
      ) : isEditing ? (
        <div className={`${
          isMobile ? 'space-y-6' : 'space-y-8'
        }`}>
          {/* Basic Configuration */}
          <div className={`${
            isMobile ? 'space-y-4' : 'space-y-6'
          }`}>
            <h4 className={`font-medium text-gray-700 ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}>Basic Configuration</h4>
            <div className={`grid grid-cols-1 ${
              isMobile ? 'gap-y-4' : 'gap-y-6 gap-x-4 sm:grid-cols-2'
            }`}>
              <InputField
                label="Configuration Name"
                name="name"
                value={ediFormData.name}
                onChange={handleInputChange}
                error={ediErrors.name}
                required
                icon={FileText}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Transport Method
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="mt-1">
                  <select
                    name="transportMethod"
                    value={ediFormData.transportMethod || ''}
                    onChange={handleInputChange}
                    className={`block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md ${
                      ediErrors.transportMethod ? 'border-red-300' : ''
                    }`}
                  >
                    <option value="">Select a method</option>
                    <option value="ftp">FTP</option>
                    <option value="sftp">SFTP</option>
                    <option value="http">HTTP</option>
                    <option value="http_webhook">HTTP Webhook</option>
                    <option value="as2">AS2</option>
                    <option value="api">API</option>
                  </select>
                  {ediErrors.transportMethod && (
                    <p className="mt-2 text-sm text-red-600">{ediErrors.transportMethod}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Document Format
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="mt-1">
                  <select
                    name="documentFormat"
                    value={ediFormData.documentFormat || ''}
                    onChange={handleInputChange}
                    className={`block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md ${
                      ediErrors.documentFormat ? 'border-red-300' : ''
                    }`}
                  >
                    <option value="">Select a format</option>
                    <option value="x12">X12</option>
                    <option value="edifact">EDIFACT</option>
                    <option value="json">JSON</option>
                    <option value="xml">XML</option>
                    <option value="csv">CSV</option>
                    <option value="custom">Custom</option>
                  </select>
                  {ediErrors.documentFormat && (
                    <p className="mt-2 text-sm text-red-600">{ediErrors.documentFormat}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Connection Details - Only show if transport method requires it */}
          {['ftp', 'sftp', 'http', 'as2'].includes(ediFormData.transportMethod) && (
            <div className={`${
              isMobile ? 'space-y-4' : 'space-y-6'
            }`}>
              <h4 className={`font-medium text-gray-700 ${
                isMobile ? 'text-sm' : 'text-sm'
              }`}>Connection Details</h4>
              <div className={`grid grid-cols-1 ${
                isMobile ? 'gap-y-4' : 'gap-y-6 gap-x-4 sm:grid-cols-2'
              }`}>
                <InputField
                  label="Transport URL"
                  name="transportUrl"
                  value={ediFormData.transportUrl || ''}
                  onChange={handleInputChange}
                  error={ediErrors.transportUrl}
                  required
                  icon={Server}
                  placeholder={ediFormData.transportMethod === 'as2' ? 'AS2 endpoint URL' : 'Server address'}
                />
                <InputField
                  label="Port"
                  name="transportPort"
                  type="number"
                  value={ediFormData.transportPort || ''}
                  onChange={handleNumberInputChange}
                  error={ediErrors.transportPort}
                  required={['ftp', 'sftp', 'http'].includes(ediFormData.transportMethod)}
                  icon={Server}
                />
              </div>
            </div>
          )}

          {/* Credentials */}
          <div className={`${
            isMobile ? 'space-y-4' : 'space-y-6'
          }`}>
            <h4 className={`font-medium text-gray-700 ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}>Authentication</h4>
            <div className={`grid grid-cols-1 ${
              isMobile ? 'gap-y-4' : 'gap-y-6 gap-x-4 sm:grid-cols-2'
            }`}>
              <InputField
                label="Username"
                name="username"
                value={ediFormData.username || ''}
                onChange={handleInputChange}
                error={ediErrors.username}
                required={['ftp', 'sftp', 'http'].includes(ediFormData.transportMethod)}
                icon={Key}
              />
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700">
                  Password
                  {['ftp', 'sftp', 'http'].includes(ediFormData.transportMethod) && !ediFormData.ediConfigId && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={ediFormData.password || ''}
                    onChange={handleInputChange}
                    className={`block w-full pl-10 pr-10 border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      ediErrors.password ? 'border-red-300' : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {ediErrors.password && (
                  <p className="mt-2 text-sm text-red-600">{ediErrors.password}</p>
                )}
                {ediFormData.ediConfigId && (
                  <p className="mt-1 text-sm text-gray-500">
                    Leave blank to keep the existing password
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Trading Partner Information */}
          <div className={`${
            isMobile ? 'space-y-4' : 'space-y-6'
          }`}>
            <h4 className={`font-medium text-gray-700 ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}>Trading Partner Information</h4>
            <div className={`grid grid-cols-1 ${
              isMobile ? 'gap-y-4' : 'gap-y-6 gap-x-4 sm:grid-cols-2'
            }`}>
              <InputField
                label="Trading Partner ID"
                name="tradingPartnerId"
                value={ediFormData.tradingPartnerId || ''}
                onChange={handleInputChange}
                error={ediErrors.tradingPartnerId}
                required
                icon={Building}
              />
              <InputField
                label="Trading Partner Qualifier"
                name="tradingPartnerQualifier"
                value={ediFormData.tradingPartnerQualifier || ''}
                onChange={handleInputChange}
                error={ediErrors.tradingPartnerQualifier}
                required
                icon={Building}
              />
            </div>
          </div>

          {/* File Naming */}
          <div className={`${
            isMobile ? 'space-y-4' : 'space-y-6'
          }`}>
            <h4 className={`font-medium text-gray-700 ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}>File Configuration</h4>
            <div className={`grid grid-cols-1 ${
              isMobile ? 'gap-y-4' : 'gap-y-6 gap-x-4 sm:grid-cols-2'
            }`}>
              <InputField
                label="Incoming File Pattern"
                name="incomingFilePattern"
                value={ediFormData.incomingFilePattern || ''}
                onChange={handleInputChange}
                icon={FileText}
              />
              <InputField
                label="Outgoing File Convention"
                name="outgoingFileNaming"
                value={ediFormData.outgoingFileNaming || ''}
                onChange={handleInputChange}
                icon={FileText}
              />
            </div>
          </div>

          {/* Advanced Settings */}
          <div className={`${
            isMobile ? 'space-y-4' : 'space-y-6'
          }`}>
            <div className={`flex items-center justify-between ${
              isMobile ? 'flex-col items-start space-y-2' : ''
            }`}>
              <h4 className={`font-medium text-gray-700 ${
                isMobile ? 'text-sm' : 'text-sm'
              }`}>Advanced Settings</h4>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className={`text-blue-600 hover:text-blue-500 ${
                  isMobile ? 'text-sm self-end' : 'text-sm'
                }`}
              >
                {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
              </button>
            </div>
            
            {showAdvanced && (
              <div className={`grid grid-cols-1 ${
                isMobile ? 'gap-y-4' : 'gap-y-6 gap-x-4 sm:grid-cols-2'
              }`}>
                <div className={`${
                  isMobile ? 'space-y-3' : 'space-y-4'
                }`}>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="autoProcessIncoming"
                      checked={ediFormData.autoProcessIncoming ?? true}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      Auto-process incoming files
                    </span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="acknowledgmentRequired"
                      checked={ediFormData.acknowledgmentRequired ?? true}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      Require acknowledgments
                    </span>
                  </label>
                </div>
                
                <div className={`${
                  isMobile ? 'space-y-3' : 'space-y-4'
                }`}>
                  <InputField
                    label="Retry Count"
                    name="retryCount"
                    type="number"
                    value={ediFormData.retryCount ?? 3}
                    onChange={handleNumberInputChange}
                    min={1}
                    max={10}
                    icon={Network}
                  />
                  
                  <InputField
                    label="Retry Interval (minutes)"
                    name="retryIntervalMinutes"
                    type="number"
                    value={ediFormData.retryIntervalMinutes ?? 15}
                    onChange={handleNumberInputChange}
                    min={1}
                    max={60}
                    icon={Clock}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className={`text-gray-500 ${
          isMobile ? 'text-sm' : 'text-sm'
        }`}>
          Click "Edit EDI" to configure EDI settings
        </p>
      )}

      {/* Add save button at the bottom */}
      {isEditing && (
        <div className={`flex ${
          isMobile ? 'mt-4 justify-center' : 'mt-6 justify-end'
        }`}>
          <button
            type="button"
            onClick={handleSaveConfig}
            disabled={savingConfig}
            className={`inline-flex items-center border border-transparent shadow-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              isMobile ? 'px-4 py-2 text-sm w-full justify-center' : 'px-4 py-2 text-sm'
            } ${
              savingConfig
                ? 'bg-blue-400'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {savingConfig ? (
              <>
                <span className="animate-spin mr-2">⟳</span>
                Saving...
              </>
            ) : (
              <>
                <Save className={`mr-1.5 ${
                  isMobile ? 'w-4 h-4' : 'w-4 h-4'
                }`} />
                {isMobile ? 'Save Config' : 'Save Configuration'}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * SamsaraSection - Samsara OAuth 2.0 Integration for Carriers
 * 
 * Allows carriers to connect their Samsara account for:
 * - Real-time vehicle telematics (GPS, speed, fuel)
 * - Driver information and status
 * - ELD compliance data
 * - Fleet management integration
 */
const SamsaraSection = () => {
  const { isMobile } = useScreenSize();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [integration, setIntegration] = useState(null);
  const [error, setError] = useState(null);

  // Check for OAuth callback result on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const samsaraConnected = urlParams.get('samsara_connected');
    const samsaraError = urlParams.get('samsara_error');
    const errorDescription = urlParams.get('description');

    if (samsaraConnected === 'true') {
      toast.success('Successfully connected to Samsara!');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (samsaraError) {
      const errorMessages = {
        'invalid_state': 'Session expired. Please try connecting again.',
        'state_expired': 'Connection request expired. Please try again.',
        'token_exchange_failed': 'Failed to complete connection. Please try again.',
        'missing_tokens': 'Connection failed. Please try again.',
        'missing_params': 'Invalid callback. Please try again.',
        'internal_error': 'An unexpected error occurred. Please try again.'
      };
      toast.error(errorMessages[samsaraError] || errorDescription || 'Failed to connect to Samsara');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    fetchStatus();
  }, []);

  // Fetch current integration status
  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await samsaraApi.getStatus();
      setIntegration(data.integration);
    } catch (err) {
      console.error('Error fetching Samsara status:', err);
      setError('Failed to load integration status');
    } finally {
      setLoading(false);
    }
  };

  // Initiate OAuth flow
  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      // Pass the current URL so the backend knows where to redirect back to
      const returnUrl = window.location.href.split('?')[0]; // Remove any existing query params
      const { data } = await samsaraApi.initiateOAuth({ return_url: returnUrl });
      // Redirect to Samsara authorization page
      window.location.href = data.authorization_url;
    } catch (err) {
      console.error('Error initiating Samsara OAuth:', err);
      const errorMessage = err.response?.data?.error || 'Failed to initiate connection';
      setError(errorMessage);
      toast.error(errorMessage);
      setConnecting(false);
    }
  };

  // Disconnect integration
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Samsara? This will stop syncing telematics data.')) {
      return;
    }
    
    setDisconnecting(true);
    setError(null);
    try {
      await samsaraApi.revokeAccess();
      toast.success('Samsara disconnected successfully');
      await fetchStatus();
    } catch (err) {
      console.error('Error disconnecting Samsara:', err);
      const errorMessage = err.response?.data?.error || 'Failed to disconnect';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setDisconnecting(false);
    }
  };

  // Test connection
  const handleTestConnection = async () => {
    setTesting(true);
    setError(null);
    try {
      const { data } = await samsaraApi.testConnection();
      if (data.success) {
        // Use the integration data from the response directly
        if (data.integration) {
          setIntegration(data.integration);
        }
        
        if (data.limited_scopes) {
          // Connected but with limited permissions
          toast.warning('Connected with limited permissions. Some features may not be available.');
          setError('Limited scopes: Check your Samsara OAuth app permissions to enable all features.');
        } else {
          toast.success(`Connection verified! Organization: ${data.organization?.name || 'Connected'}`);
        }
      } else {
        // Check if we need to show reconnect message
        if (data.needs_reconnect) {
          setError('Connection lost. Please reconnect to Samsara.');
          if (data.integration) {
            setIntegration(data.integration);
          }
        }
        throw new Error(data.error || 'Test failed');
      }
    } catch (err) {
      console.error('Error testing Samsara connection:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Connection test failed';
      setError(errorMessage);
      toast.error(errorMessage);
      // Update integration state if returned in error response
      if (err.response?.data?.integration) {
        setIntegration(err.response.data.integration);
      }
    } finally {
      setTesting(false);
    }
  };

  // Refresh token
  const handleRefreshToken = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const { data } = await samsaraApi.refreshToken();
      toast.success('Token refreshed successfully');
      // Use the integration data from the response directly
      if (data.integration) {
        setIntegration(data.integration);
      } else {
        // Fallback to fetching status if integration not in response
        await fetchStatus();
      }
    } catch (err) {
      console.error('Error refreshing Samsara token:', err);
      const errorMessage = err.response?.data?.error || 'Failed to refresh token';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setRefreshing(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Connected' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      expired: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Token Expired' },
      revoked: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Disconnected' },
      error: { bg: 'bg-red-100', text: 'text-red-800', label: 'Error' }
    };
    
    const config = statusConfig[status] || statusConfig.error;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {status === 'active' && <Activity className="w-3 h-3 mr-1" />}
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className={`${isMobile ? 'px-4 py-4' : 'px-6 py-6'}`}>
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin mr-3">⟳</div>
          <span>Loading Samsara integration status...</span>
        </div>
      </div>
    );
  }

  const isConnected = integration?.status === 'active';
  const needsRefresh = integration?.is_token_expired || integration?.status === 'expired';

  return (
    <div className={`${isMobile ? 'px-4 py-4' : 'px-6 py-6'}`}>
      {/* Header */}
      <div className={`mb-6 ${isMobile ? 'space-y-3' : 'flex items-start justify-between'}`}>
        <div className="flex items-start space-x-3">
          <div className={`flex-shrink-0 ${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg`}>
            <Truck className={`text-white ${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
          </div>
          <div>
            <h3 className={`font-semibold text-gray-900 ${isMobile ? 'text-base' : 'text-lg'}`}>
              Samsara Integration
            </h3>
            <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              Connect your Samsara account for real-time telematics and fleet data
            </p>
          </div>
        </div>
        {integration && (
          <StatusBadge status={integration.status} />
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className={`mb-4 p-3 rounded-lg bg-red-50 border border-red-200 ${isMobile ? 'text-sm' : ''}`}>
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Not connected state - show for null, error, revoked, expired, or pending */}
      {!isConnected && (
        <div className={`rounded-lg border-2 border-dashed ${
          integration?.status === 'error' ? 'border-red-300 bg-red-50/30' : 'border-gray-300'
        } ${isMobile ? 'p-4' : 'p-8'}`}>
          <div className="text-center">
            <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
              integration?.status === 'error' ? 'bg-red-100' : 'bg-gray-100'
            }`}>
              <Link className={`w-6 h-6 ${integration?.status === 'error' ? 'text-red-400' : 'text-gray-400'}`} />
            </div>
            <h4 className={`font-medium mb-2 ${isMobile ? 'text-sm' : ''} ${
              integration?.status === 'error' ? 'text-red-900' : 'text-gray-900'
            }`}>
              {integration?.status === 'error' 
                ? 'Connection Failed - Try Again' 
                : integration?.status === 'revoked'
                  ? 'Samsara Disconnected'
                  : 'Connect Your Samsara Account'}
            </h4>
            <p className={`text-gray-500 mb-6 max-w-md mx-auto ${isMobile ? 'text-xs' : 'text-sm'}`}>
              {integration?.status === 'error' 
                ? 'The previous connection attempt failed. Please try connecting again.'
                : 'Link your Samsara account to enable automatic telematics updates, real-time vehicle tracking, and driver status synchronization.'}
            </p>
            
            {/* Features list */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'} mb-6 text-left max-w-lg mx-auto`}>
              {[
                { icon: MapPin, text: 'Real-time GPS tracking' },
                { icon: Activity, text: 'Live vehicle telematics' },
                { icon: User, text: 'Driver status & HOS' },
                { icon: Zap, text: 'Automatic data sync' }
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center text-gray-600">
                  <feature.icon className={`flex-shrink-0 text-blue-500 mr-2 ${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
                  <span className={isMobile ? 'text-xs' : 'text-sm'}>{feature.text}</span>
                </div>
              ))}
            </div>

            <button
              onClick={handleConnect}
              disabled={connecting}
              className={`inline-flex items-center justify-center border border-transparent shadow-sm font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isMobile ? 'px-4 py-3 text-sm w-full' : 'px-6 py-3 text-sm'
              } ${connecting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {connecting ? (
                <>
                  <span className="animate-spin mr-2">⟳</span>
                  Connecting...
                </>
              ) : integration?.status === 'error' || integration?.status === 'pending' ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Connection
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect to Samsara
                </>
              )}
            </button>
            
            <p className={`mt-3 text-gray-400 ${isMobile ? 'text-xs' : 'text-xs'}`}>
              {integration?.status === 'error' 
                ? 'Previous attempt failed. Click to try again.'
                : "You'll be redirected to Samsara to authorize the connection"}
            </p>
          </div>
        </div>
      )}

      {/* Connected state */}
      {isConnected && (
        <div className="space-y-6">
          {/* Connection info card */}
          <div className={`bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 ${isMobile ? 'p-4' : 'p-6'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <span className={`font-medium text-green-800 ${isMobile ? 'text-sm' : ''}`}>
                  Connected to Samsara
                </span>
              </div>
              {integration.samsara_org_name && (
                <span className={`text-green-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {integration.samsara_org_name}
                </span>
              )}
            </div>

            {/* Integration details */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'}`}>
              <div className="bg-white/60 rounded-lg p-3">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Organization ID</span>
                <p className={`font-mono text-gray-700 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                  {integration.samsara_org_id || 'N/A'}
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Connected Since</span>
                <p className={`text-gray-700 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                  {integration.connected_at 
                    ? new Date(integration.connected_at).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Last Synced</span>
                <p className={`text-gray-700 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                  {integration.last_synced_at 
                    ? new Date(integration.last_synced_at).toLocaleString()
                    : 'Never'}
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Token Expires</span>
                <p className={`${needsRefresh ? 'text-orange-600' : 'text-gray-700'} ${isMobile ? 'text-sm' : 'text-sm'}`}>
                  {integration.token_expires_at 
                    ? new Date(integration.token_expires_at).toLocaleString()
                    : 'N/A'}
                </p>
              </div>
            </div>

            {/* Scopes */}
            {integration.scopes && integration.scopes.length > 0 && (
              <div className="mt-4">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Permissions</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {integration.scopes.map((scope, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                      {scope}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Token refresh warning */}
          {needsRefresh && (
            <div className={`p-4 rounded-lg bg-orange-50 border border-orange-200 ${isMobile ? 'text-sm' : ''}`}>
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-orange-800">Token Refresh Required</h4>
                  <p className="text-orange-700 text-sm mt-1">
                    Your access token has expired. Click refresh to continue syncing data.
                  </p>
                  <button
                    onClick={handleRefreshToken}
                    disabled={refreshing}
                    className={`mt-3 inline-flex items-center px-3 py-1.5 border border-orange-300 rounded-md text-sm font-medium text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 ${
                      refreshing ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {refreshing ? (
                      <>
                        <span className="animate-spin mr-2">⟳</span>
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1.5" />
                        Refresh Token
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'space-x-3'}`}>
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className={`inline-flex items-center justify-center border border-gray-300 shadow-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isMobile ? 'px-4 py-2 text-sm' : 'px-4 py-2 text-sm'
              } ${testing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {testing ? (
                <>
                  <span className="animate-spin mr-2">⟳</span>
                  Testing...
                </>
              ) : (
                <>
                  <Activity className="w-4 h-4 mr-1.5" />
                  Test Connection
                </>
              )}
            </button>

            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className={`inline-flex items-center justify-center border border-red-300 shadow-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                isMobile ? 'px-4 py-2 text-sm' : 'px-4 py-2 text-sm'
              } ${disconnecting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {disconnecting ? (
                <>
                  <span className="animate-spin mr-2">⟳</span>
                  Disconnecting...
                </>
              ) : (
                <>
                  <Unlink className="w-4 h-4 mr-1.5" />
                  Disconnect
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Help section */}
      <div className={`mt-8 pt-6 border-t border-gray-200 ${isMobile ? 'text-xs' : 'text-sm'}`}>
        <h4 className={`font-medium text-gray-700 mb-2 ${isMobile ? 'text-sm' : ''}`}>
          About Samsara Integration
        </h4>
        <p className="text-gray-500">
          This integration uses OAuth 2.0 to securely connect to your Samsara account. 
          We only request read access to your fleet data. You can revoke access at any time 
          from this page or from your Samsara dashboard.
        </p>
        <a 
          href="https://www.samsara.com/products/platform" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center mt-2 text-blue-600 hover:text-blue-700"
        >
          Learn more about Samsara
          <ExternalLink className="w-3 h-3 ml-1" />
        </a>
      </div>
    </div>
  );
};


const RestrictedInputField = ({ label, value, icon: Icon }) => {
  const { isMobile } = useScreenSize();
  
  return (
    <div className="relative">
      <label className={`block font-medium text-gray-700 ${
        isMobile ? 'text-sm mb-2' : 'text-sm'
      }`}>
        {label}
        <span className={`ml-1 text-gray-400 ${
          isMobile ? 'text-xs' : ''
        }`}>(Restricted)</span>
      </label>
      <div className={`relative rounded-md shadow-sm ${
        isMobile ? '' : 'mt-1'
      }`}>
        <div className={`absolute inset-y-0 left-0 flex items-center pointer-events-none ${
          isMobile ? 'pl-3' : 'pl-3'
        }`}>
          <Icon className={`text-gray-400 ${
            isMobile ? 'h-4 w-4' : 'h-5 w-5'
          }`} />
        </div>
        <input
          type="text"
          value={value}
          disabled
          className={`block w-full pr-10 rounded-md bg-gray-50 text-gray-500 border-gray-200 ${
            isMobile ? 'pl-10 py-3 text-base' : 'pl-10 py-2 sm:text-sm'
          }`}
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          <span className="text-gray-400 hover:text-gray-500">
            <Lock className={`${
              isMobile ? 'h-4 w-4' : 'h-5 w-5'
            }`} title="This field cannot be modified" />
          </span>
        </div>
      </div>
      <p className={`mt-1 text-gray-500 ${
        isMobile ? 'text-xs' : 'text-sm'
      }`}>
        Contact support to update this information
      </p>
    </div>
  );
};

const Settings = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [isProfileEditing, setIsProfileEditing] = useState(false);
  const [isNotificationEditing, setIsNotificationEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const organizationAddresses = useSelector(selectOrganizationAddresses);
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    number: false,
    special: false,
    uppercase: false,
    lowercase: false,
    noCommon: true
  });
  const [activeSection, setActiveSection] = useState('profile');
  const [ediSaveHandler, setEdiSaveHandler] = useState(null);
  const [isSavingEdi, setIsSavingEdi] = useState(false);

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);
  
  const isAdmin = user.role.endsWith('admin');
  const isCustomer = user.role.startsWith('customer');
  
  // Simplified allowedFields based on user role
  const allowedFields = {
    firstName: true,
    lastName: true,
    phone: true,
    phoneExtension: true,
    bio: true,
    preferredCommunication: true,
    avatar: true,
    // Only include address-related fields for admins
    ...(isAdmin && {
      companyAddress: true,
      companyCity: true,
      companyState: true,
      companyZip: true
    })
  };

  // Update the initial form data setup
  const [formData, setFormData] = useState(() => {
    // Find primary physical and billing addresses from organization data
    const physicalAddress = organizationAddresses.find(addr => addr.address_type === 'physical') || {};
    const billingAddress = organizationAddresses.find(addr => addr.address_type === 'billing') || {};

    return {
      // Personal Information
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      phoneExtension: user.phoneExtension || '',
      bio: user.bio || '',
      preferredCommunication: user.preferredCommunication || '',
      avatar: user.avatar || null,
      
      // Company Information
      company: user.companyName || '',
      
      // Physical Address - use null coalescing to handle undefined properties
      physicalStreetAddress: physicalAddress?.street_address ?? '',
      physicalSuite: physicalAddress?.suite ?? '',
      physicalCity: physicalAddress?.city ?? '',
      physicalState: physicalAddress?.state ?? '',
      physicalZipCode: physicalAddress?.zip_code ?? '',
      
      // Billing Address
      billingStreetAddress: billingAddress?.street_address ?? '',
      billingSuite: billingAddress?.suite ?? '',
      billingCity: billingAddress?.city ?? '',
      billingState: billingAddress?.state ?? '',
      billingZipCode: billingAddress?.zip_code ?? '',
    };
  });
  const [errors, setErrors] = useState({});

  // Add password validation effect
  useEffect(() => {
    const commonPatterns = [
      'password', '123456', 'qwerty', 'admin', 'letmein', 
      'welcome', 'monkey', 'football', 'abc123'
    ];

    if (formData.newPassword) {
      setPasswordValidations({
        length: formData.newPassword.length >= 12,
        number: /\d/.test(formData.newPassword),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword),
        uppercase: /[A-Z]/.test(formData.newPassword),
        lowercase: /[a-z]/.test(formData.newPassword),
        noCommon: !commonPatterns.some(pattern => 
          formData.newPassword.toLowerCase().includes(pattern)
        )
      });
    }
  }, [formData.newPassword]);

  const validateForm = () => {
    const newErrors = {};
    
    // Only check for changes in editable fields
    const editableFields = Object.keys(allowedFields).filter(field => 
        allowedFields[field] === true
    );
    
    // Check if at least one editable field has been modified
    const hasChanges = editableFields.some(field => 
        formData[field] !== user[field]
    );

    if (!hasChanges && !formData.newPassword) {
        toast.warning('Please make at least one change before saving');
        return false;
    }

    // Password validation
    if (formData.newPassword || formData.currentPassword || formData.confirmPassword) {
        if (!formData.currentPassword) {
            newErrors.currentPassword = 'Current password is required to change password';
        }
        if (!formData.newPassword) {
            newErrors.newPassword = 'New password is required';
        }
        if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        if (!Object.values(passwordValidations).every(Boolean)) {
            newErrors.newPassword = 'Password does not meet all requirements';
        }
    }

    // Only validate editable fields
    if (allowedFields.firstName && formData.firstName?.trim() === '') {
        newErrors.firstName = 'First name cannot be empty';
    }
    if (allowedFields.lastName && formData.lastName?.trim() === '') {
        newErrors.lastName = 'Last name cannot be empty';
    }
    if (allowedFields.phone && formData.phone?.trim() === '') {
        newErrors.phone = 'Phone number cannot be empty';
    }

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
        const firstError = Object.values(newErrors)[0];
        toast.error(firstError, {
            position: isMobile ? "bottom-center" : "top-right",
            autoClose: 5000
        });
        return false;
    }
    
    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Create the request payload
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      phoneExtension: formData.phoneExtension,
      bio: formData.bio,
      preferredCommunication: formData.preferredCommunication,
      avatar: formData.avatar
    };

    // Add password fields if they exist
    if (formData.newPassword) {
      payload.currentPassword = formData.currentPassword;
      payload.newPassword = formData.newPassword;
    }

    // Add address updates for admin users
    if (isAdmin) {
      // Only include addresses if at least one address has required fields
      const addresses = [];
      
      // Physical address
      if (formData.physicalStreetAddress?.trim() && 
          formData.physicalCity?.trim() && 
          formData.physicalState?.trim() && 
          formData.physicalZipCode?.trim()) {
        addresses.push({
          address_type: 'physical',
          street_address: formData.physicalStreetAddress.trim(),
          suite: formData.physicalSuite?.trim() || null,
          city: formData.physicalCity.trim(),
          state: formData.physicalState.trim(),
          zip_code: formData.physicalZipCode.trim(),
          country: 'USA'
        });
      }
      
      // Billing address
      if (formData.billingStreetAddress?.trim() && 
          formData.billingCity?.trim() && 
          formData.billingState?.trim() && 
          formData.billingZipCode?.trim()) {
        addresses.push({
          address_type: 'billing',
          street_address: formData.billingStreetAddress.trim(),
          suite: formData.billingSuite?.trim() || null,
          city: formData.billingCity.trim(),
          state: formData.billingState.trim(),
          zip_code: formData.billingZipCode.trim(),
          country: 'USA'
        });
      }
      
      // Only include addresses in payload if there are valid addresses
      if (addresses.length > 0) {
        payload.addresses = addresses;
      }
    }

    setLoading(true);
    const toastId = toast.loading('Updating profile...', {
        position: isMobile ? "bottom-center" : "top-right"
    });
    
    try {
        await dispatch(updateUserProfile(payload)).unwrap();
        
        toast.update(toastId, {
            render: 'Profile updated successfully',
            type: 'success',
            isLoading: false,
            autoClose: 3000
        });
        
        // Clear password fields after successful save
        setFormData(prev => ({
            ...prev,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        }));
        
        setIsProfileEditing(false);
    } catch (error) {
        // Extract detailed error information from various response formats
        let errorMessage = 'Failed to update profile';
        const errorData = error.response?.data;
        
        if (errorData) {
            // Handle string response
            if (typeof errorData === 'string') {
                errorMessage = errorData;
            } 
            // Handle object response with error field
            else if (errorData.error) {
                errorMessage = errorData.error;
            }
            // Handle object response with message field
            else if (errorData.message) {
                errorMessage = errorData.message;
            }
            
            // Append validation details if available
            if (errorData.details) {
                if (typeof errorData.details === 'string') {
                    errorMessage += `: ${errorData.details}`;
                } else if (typeof errorData.details === 'object') {
                    // Format field-specific validation errors
                    const detailMessages = Object.entries(errorData.details)
                        .map(([field, msg]) => `${field}: ${msg}`)
                        .join('; ');
                    if (detailMessages) {
                        errorMessage += ` (${detailMessages})`;
                    }
                }
            }
            
            // Append field info if available
            if (errorData.field && !errorMessage.includes(errorData.field)) {
                errorMessage = `${errorData.field}: ${errorMessage}`;
            }
        } else if (error.message) {
            // Handle network/axios errors
            if (error.message.includes('Network Error')) {
                errorMessage = 'Network error - please check your connection and try again';
            } else if (error.message.includes('timeout')) {
                errorMessage = 'Request timed out - please try again';
            } else {
                errorMessage = error.message;
            }
        }
        
        toast.update(toastId, {
            render: errorMessage,
            type: 'error',
            isLoading: false,
            autoClose: 5000
        });
    } finally {
        setLoading(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          avatar: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const renderCompanyAddressSection = (type) => {
    const isPhysical = type === 'physical';
    const prefix = isPhysical ? 'physical' : 'billing';
    const title = isPhysical ? 'Physical Address' : 'Billing Address';

    return (
      <div className={`${
        isMobile ? 'space-y-4' : 'space-y-6'
      }`}>
        <h4 className={`font-medium text-gray-700 ${
          isMobile ? 'text-sm' : 'text-md'
        }`}>{title}</h4>
        <div className={`grid grid-cols-1 ${
          isMobile ? 'gap-y-4' : 'gap-y-6 gap-x-4 sm:grid-cols-6'
        }`}>
          <div className={`${
            isMobile ? 'col-span-1' : 'sm:col-span-4'
          }`}>
            <InputField
              label="Street Address"
              name={`${prefix}StreetAddress`}
              value={formData[`${prefix}StreetAddress`]}
              onChange={handleInputChange}
              error={errors[`${prefix}StreetAddress`]}
              disabled={!isProfileEditing}
              icon={MapPin}
            />
          </div>
          <div className={`${
            isMobile ? 'col-span-1' : 'sm:col-span-2'
          }`}>
            <InputField
              label="Suite/Unit"
              name={`${prefix}Suite`}
              value={formData[`${prefix}Suite`]}
              onChange={handleInputChange}
              error={errors[`${prefix}Suite`]}
              disabled={!isProfileEditing}
              icon={MapPin}
            />
          </div>
          <div className={`${
            isMobile ? 'col-span-1' : 'sm:col-span-2'
          }`}>
            <InputField
              label="City"
              name={`${prefix}City`}
              value={formData[`${prefix}City`]}
              onChange={handleInputChange}
              error={errors[`${prefix}City`]}
              disabled={!isProfileEditing}
              icon={MapPin}
            />
          </div>
          <div className={`grid ${
            isMobile ? 'grid-cols-2 gap-x-3' : 'sm:col-span-2'
          }`}>
            <InputField
              label="State"
              name={`${prefix}State`}
              value={formData[`${prefix}State`]}
              onChange={handleInputChange}
              error={errors[`${prefix}State`]}
              disabled={!isProfileEditing}
              icon={MapPin}
            />
          </div>
          <div className={`${
            isMobile ? 'col-span-1' : 'sm:col-span-2'
          }`}>
            <InputField
              label="ZIP Code"
              name={`${prefix}ZipCode`}
              value={formData[`${prefix}ZipCode`]}
              onChange={handleInputChange}
              error={errors[`${prefix}ZipCode`]}
              disabled={!isProfileEditing}
              icon={MapPin}
            />
          </div>
        </div>
      </div>
    );
  };

  // Add this useEffect to update form data when addresses change
  useEffect(() => {
    const physicalAddress = organizationAddresses.find(addr => addr.address_type === 'physical') || {};
    const billingAddress = organizationAddresses.find(addr => addr.address_type === 'billing') || {};

    setFormData(prev => ({
      ...prev,
      physicalStreetAddress: physicalAddress?.street_address ?? prev.physicalStreetAddress,
      physicalSuite: physicalAddress?.suite ?? prev.physicalSuite,
      physicalCity: physicalAddress?.city ?? prev.physicalCity,
      physicalState: physicalAddress?.state ?? prev.physicalState,
      physicalZipCode: physicalAddress?.zip_code ?? prev.physicalZipCode,
      
      billingStreetAddress: billingAddress?.street_address ?? prev.billingStreetAddress,
      billingSuite: billingAddress?.suite ?? prev.billingSuite,
      billingCity: billingAddress?.city ?? prev.billingCity,
      billingState: billingAddress?.state ?? prev.billingState,
      billingZipCode: billingAddress?.zip_code ?? prev.billingZipCode,
    }));
  }, [organizationAddresses]);

  // Add EDI to navigation for customers, Samsara for carriers
  const isCarrier = user.role.startsWith('carrier');
  const tabs = [
    { id: 'profile', label: 'Profile Settings' },
    { id: 'notifications', label: 'Notification Preferences' },
    ...(isCustomer ? [{ id: 'edi', label: 'EDI Configuration' }] : []),
    ...(isCarrier ? [{ id: 'samsara', label: 'Samsara Integration' }] : [])
  ];

  // Helper to cancel profile editing and clear password fields
  const handleCancelProfileEdit = () => {
    setFormData(prev => ({
      ...prev,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }));
    setErrors({});
    setIsProfileEditing(false);
  };

  // Function to render the appropriate action buttons based on active section
  const renderActionButtons = () => {
    switch (activeSection) {
      case 'profile':
        return isProfileEditing ? (
          <div className={`flex ${
            isMobile ? 'space-x-2 w-full' : 'space-x-3'
          }`}>
            <button
              type="button"
              onClick={handleCancelProfileEdit}
              className={`inline-flex items-center border border-gray-300 shadow-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isMobile ? 'px-3 py-2 text-sm flex-1 justify-center' : 'px-3 py-1.5 text-sm'
              }`}
            >
              <X className={`mr-1.5 ${
                isMobile ? 'w-4 h-4' : 'w-4 h-4'
              }`} />
              Cancel
            </button>
            <button
              type="submit"
              form="settings-form"
              disabled={loading}
              className={`inline-flex items-center border border-transparent shadow-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isMobile ? 'px-3 py-2 text-sm flex-1 justify-center' : 'px-3 py-1.5 text-sm'
              }`}
            >
              <Save className={`mr-1.5 ${
                isMobile ? 'w-4 h-4' : 'w-4 h-4'
              }`} />
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsProfileEditing(true)}
            className={`inline-flex items-center border border-transparent shadow-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              isMobile ? 'px-4 py-2 text-sm w-full justify-center' : 'px-3 py-1.5 text-sm'
            }`}
          >
            Edit Profile
          </button>
        );

      case 'notifications':
        return isNotificationEditing ? (
          <div className={`flex ${
            isMobile ? 'space-x-2 w-full' : 'space-x-3'
          }`}>
            <button
              type="button"
              onClick={() => setIsNotificationEditing(false)}
              className={`inline-flex items-center border border-gray-300 shadow-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isMobile ? 'px-3 py-2 text-sm flex-1 justify-center' : 'px-3 py-1.5 text-sm'
              }`}
            >
              <X className={`mr-1.5 ${
                isMobile ? 'w-4 h-4' : 'w-4 h-4'
              }`} />
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {/* Add your notification save handler */}}
              className={`inline-flex items-center border border-transparent shadow-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isMobile ? 'px-3 py-2 text-sm flex-1 justify-center' : 'px-3 py-1.5 text-sm'
              }`}
            >
              <Save className={`mr-1.5 ${
                isMobile ? 'w-4 h-4' : 'w-4 h-4'
              }`} />
              Save
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsNotificationEditing(true)}
            className={`inline-flex items-center border border-transparent shadow-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              isMobile ? 'px-4 py-2 text-sm w-full justify-center' : 'px-3 py-1.5 text-sm'
            }`}
          >
            Edit Notifications
          </button>
        );

      case 'edi':
        return isProfileEditing ? (
          <div className={`flex ${
            isMobile ? 'space-x-2 w-full' : 'space-x-3'
          }`}>
            <button
              type="button"
              onClick={() => setIsProfileEditing(false)}
              className={`inline-flex items-center border border-gray-300 shadow-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isMobile ? 'px-3 py-2 text-sm flex-1 justify-center' : 'px-3 py-1.5 text-sm'
              }`}
            >
              <X className={`mr-1.5 ${
                isMobile ? 'w-4 h-4' : 'w-4 h-4'
              }`} />
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (ediSaveHandler && typeof ediSaveHandler === 'function') {
                  setIsSavingEdi(true);
                  ediSaveHandler()
                    .finally(() => setIsSavingEdi(false));
                } else {
                  toast.error("Save function not available");
                }
              }}
              disabled={isSavingEdi}
              className={`inline-flex items-center border border-transparent shadow-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isMobile ? 'px-3 py-2 text-sm flex-1 justify-center' : 'px-3 py-1.5 text-sm'
              }`}
            >
              {isSavingEdi ? (
                <>
                  <span className="animate-spin mr-2">⟳</span>
                  Saving...
                </>
              ) : (
                <>
                  <Save className={`mr-1.5 ${
                    isMobile ? 'w-4 h-4' : 'w-4 h-4'
                  }`} />
                  Save
                </>
              )}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsProfileEditing(true)}
            className={`inline-flex items-center border border-transparent shadow-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              isMobile ? 'px-4 py-2 text-sm w-full justify-center' : 'px-3 py-1.5 text-sm'
            }`}
          >
            Edit EDI
          </button>
        );

      default:
        return null;
    }
  };

  // Inside the Settings component, add a helper function to get the default country:
  const getDefaultCountry = (phoneNumber) => {
    // If phone starts with +1, it's US/Canada
    if (phoneNumber?.startsWith('+1')) return 'us';
    // If phone starts with +, try to extract country code
    if (phoneNumber?.startsWith('+')) {
      // This is a simplified approach - in production, you'd use a proper library
      // to determine the country from the dial code
      return 'us'; // Default fallback
    }
    return 'us'; // Default to US for backward compatibility
  };

  // Add a format phone helper function similar to the one in DriverManagement.js
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return 'N/A';
    
    // Handle E.164 format (starting with '+')
    if (phone.startsWith('+')) {
      // Try to format it nicely if possible
      try {
        // Simple formatter for common formats
        if (phone.startsWith('+1') && phone.length === 12) {
          // US/Canada format: +1 (XXX) XXX-XXXX
          return `+1 (${phone.substring(2, 5)}) ${phone.substring(5, 8)}-${phone.substring(8)}`;
        }
        // For other countries, just add spaces for readability
        return phone.replace(/(\+\d{1,3})(\d{3})(\d{3})(\d{4})/, '$1 $2 $3 $4');
      } catch (e) {
        return phone; // Fall back to raw format
      }
    }
    
    // Legacy format (no +), assume US
    if (phone.length === 10) {
      return `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6)}`;
    }
    
    // Return as-is for other formats
    return phone;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <SideNavigation isOpen={sidebarOpen} onToggle={toggleSidebar} />
      
      <main className={`flex-1 overflow-y-auto transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-16')
      }`}>
        <div className={`mx-auto ${
          isMobile ? 'px-2 py-4 max-w-full' : 'max-w-3xl p-4 sm:p-6 lg:p-8'
        }`}>
          {/* Mobile menu button */}
          {(isMobile || isTablet) && (
            <button
              onClick={toggleSidebar}
              className="mb-3 p-2 bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}

          <div className={`bg-white shadow-sm overflow-hidden ${
            isMobile ? 'rounded-lg' : 'rounded-2xl'
          }`}>
            <div className={`border-b border-gray-200 ${
              isMobile ? 'px-4 py-3' : 'px-6 py-4'
            }`}>
              <h1 className={`font-bold text-gray-900 ${
                isMobile ? 'text-xl' : 'text-2xl'
              }`}>
                {isCustomer ? 'Customer' : 'Carrier'} Settings
              </h1>
              <p className={`mt-1 text-gray-500 ${
                isMobile ? 'text-xs' : 'text-sm'
              }`}>
                Manage your profile information{isAdmin && ' and company details'}
              </p>
            </div>

            {/* Updated tab navigation with context-aware edit button */}
            <div className={`border-b border-gray-200 ${
              isMobile ? 'px-0 pt-3' : 'px-6 pt-4'
            }`}>
              <div className={`${
                isMobile ? 'flex flex-col space-y-3' : 'flex items-center justify-between'
              }`}>
                {/* Mobile tab navigation with horizontal scroll */}
                <nav className={`-mb-px flex ${
                  isMobile ? 'overflow-x-auto scrollbar-hide px-4 space-x-6' : 'space-x-8'
                }`}>
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveSection(tab.id)}
                      className={`${
                        activeSection === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap pb-3 px-1 border-b-2 font-medium ${
                        isMobile ? 'text-sm min-w-max' : 'text-sm'
                      }`}
                    >
                      {isMobile ? tab.label.replace(' Settings', '').replace(' Configuration', '').replace(' Preferences', '') : tab.label}
                    </button>
                  ))}
                </nav>
                
                {/* Context-aware edit buttons */}
                <div className={`${
                  isMobile ? 'px-4 pb-3' : 'pb-4'
                }`}>
                  {renderActionButtons()}
                </div>
              </div>
            </div>

            {activeSection === 'profile' ? (
              <form id="settings-form" onSubmit={handleProfileSubmit}>
                {/* Profile Photo Section */}
                <div className={`${
                  isMobile ? 'px-4 py-4' : 'px-6 py-6'
                }`}>
                  <div className={`flex items-center ${
                    isMobile ? 'space-x-4' : 'space-x-6'
                  }`}>
                    <div className="relative">
                      <div className={`rounded-full overflow-hidden bg-gray-100 ${
                        isMobile ? 'w-20 h-20' : 'w-24 h-24'
                      }`}>
                        {formData.avatar ? (
                          <img 
                            src={formData.avatar} 
                            alt="Profile" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className={`w-full h-full text-gray-400 ${
                            isMobile ? 'p-3' : 'p-4'
                          }`} />
                        )}
                      </div>
                      {isProfileEditing && (
                        <label 
                          htmlFor="avatar-upload" 
                          className={`absolute bottom-0 right-0 bg-blue-500 rounded-full cursor-pointer hover:bg-blue-600 transition-colors ${
                            isMobile ? 'p-1.5' : 'p-2'
                          }`}
                        >
                          <Camera className={`text-white ${
                            isMobile ? 'w-3 h-3' : 'w-4 h-4'
                          }`} />
                          <input
                            id="avatar-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleAvatarChange}
                          />
                        </label>
                      )}
                    </div>
                    <div>
                      <h3 className={`font-medium text-gray-900 ${
                        isMobile ? 'text-base' : 'text-lg'
                      }`}>Profile Photo</h3>
                      <p className={`text-gray-500 ${
                        isMobile ? 'text-xs' : 'text-sm'
                      }`}>
                        PNG, JPG or GIF up to 2MB
                      </p>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className={`${
                  isMobile ? 'px-4 py-4' : 'px-6 py-6'
                }`}>
                  <h3 className={`font-medium text-gray-900 mb-4 ${
                    isMobile ? 'text-base' : 'text-lg'
                  }`}>Personal Information</h3>
                  <div className={`grid grid-cols-1 gap-y-4 ${
                    isMobile ? 'gap-x-0' : 'gap-y-6 gap-x-4 sm:grid-cols-2'
                  }`}>
                    <InputField
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      error={errors.firstName}
                      disabled={!isProfileEditing}
                      icon={User}
                    />
                    <InputField
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      error={errors.lastName}
                      disabled={!isProfileEditing}
                      icon={User}
                    />
                    <RestrictedInputField
                      label="Email"
                      value={formData.email}
                      icon={Mail}
                    />
                    {/* Phone Number with Extension */}
                    <div className={`${
                      isMobile ? 'col-span-1' : 'col-span-6 sm:col-span-2'
                    }`}>
                      <label htmlFor="phone" className={`block font-medium text-gray-700 ${
                        isMobile ? 'text-sm mb-2' : 'text-sm'
                      }`}>
                        Phone Number
                      </label>
                      <div className={`${
                        isMobile ? '' : 'mt-1'
                      }`}>
                        <PhoneInput
                          country={getDefaultCountry(formData.phone)}
                          value={formData.phone}
                          onChange={(value) => {
                            // Ensure the value starts with + for E.164 format
                            const formattedNumber = value.startsWith('+') ? value : '+' + value;
                            setFormData({...formData, phone: formattedNumber});
                          }}
                          inputClass={`w-full px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isMobile ? 'py-3 text-base' : 'py-2'
                          }`}
                          containerClass="w-full"
                          enableSearch={true}
                          disableSearchIcon={true}
                          searchPlaceholder="Search country..."
                          disabled={!isProfileEditing}
                          required
                        />
                        <p className={`mt-1 text-gray-500 ${
                          isMobile ? 'text-xs' : 'text-xs'
                        }`}>Include country code for international numbers</p>
                      </div>
                    </div>
                    <div className={`${
                      isMobile ? 'col-span-1' : 'col-span-6 sm:col-span-1'
                    }`}>
                      <label htmlFor="phoneExtension" className={`block font-medium text-gray-700 ${
                        isMobile ? 'text-sm mb-2' : 'text-sm'
                      }`}>
                        Extension
                      </label>
                      <div className={`${
                        isMobile ? '' : 'mt-1'
                      }`}>
                        <input
                          type="text"
                          id="phoneExtension"
                          name="phoneExtension"
                          value={formData.phoneExtension || ''}
                          onChange={(e) => setFormData({...formData, phoneExtension: e.target.value})}
                          disabled={!isProfileEditing}
                          placeholder="e.g. 101"
                          maxLength={10}
                          className={`block w-full px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isMobile ? 'py-3 text-base' : 'py-2'
                          } ${!isProfileEditing ? 'bg-gray-50 text-gray-500' : ''}`}
                        />
                        <p className={`mt-1 text-gray-500 ${
                          isMobile ? 'text-xs' : 'text-xs'
                        }`}>For shared org numbers</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Information - Only visible to admins */}
                {isAdmin && (
                  <div className={`${
                    isMobile ? 'px-4 py-4' : 'px-6 py-6'
                  }`}>
                    <div className={`flex items-center justify-between mb-4 ${
                      isMobile ? 'flex-col items-start space-y-2' : ''
                    }`}>
                      <h3 className={`font-medium text-gray-900 ${
                        isMobile ? 'text-base' : 'text-lg'
                      }`}>Company Information</h3>
                      {!isProfileEditing && (
                        <span className={`inline-flex items-center rounded-full font-medium bg-blue-100 text-blue-800 ${
                          isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm'
                        }`}>
                          Admin Access
                        </span>
                      )}
                    </div>
                    <div className={`${
                      isMobile ? 'space-y-6' : 'space-y-8'
                    }`}>
                      <div className={`grid grid-cols-1 ${
                        isMobile ? 'gap-y-4' : 'gap-y-6 gap-x-4 sm:grid-cols-2'
                      }`}>
                        <RestrictedInputField
                          label="Company Name"
                          value={formData.company}
                          icon={Building}
                        />
                      </div>
                      
                      {/* Physical Address */}
                      {renderCompanyAddressSection('physical')}
                      
                      {/* Billing Address */}
                      {renderCompanyAddressSection('billing')}
                    </div>
                  </div>
                )}

                {/* Password Section */}
                <PasswordSection 
                  isEditing={isProfileEditing}
                  formData={formData}
                  setFormData={setFormData}
                  validations={passwordValidations}
                />
              </form>
            ) : activeSection === 'notifications' ? (
              <NotificationSettings 
                isEditing={isNotificationEditing}
                setIsEditing={setIsNotificationEditing}
              />
            ) : activeSection === 'edi' && isCustomer ? (
              <EdiSection 
                isEditing={isProfileEditing}
                formData={formData}
                setFormData={setFormData}
              />
            ) : activeSection === 'samsara' && isCarrier ? (
              <SamsaraSection />
            ) : (
              <div className="px-6 py-12 text-center">
                <p className="text-gray-500">
                  This section is not available for your account type
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <ToastContainer />
    </div>
  );
};

const InputField = ({ 
  label, 
  name, 
  value, 
  onChange, 
  error, 
  disabled, 
  icon: Icon, 
  type = "text",
  loading = false,
  required = false,
  placeholder = ""
}) => {
  const { isMobile } = useScreenSize();
  
  return (
    <div>
      <label htmlFor={name} className={`block font-medium text-gray-700 ${
        isMobile ? 'text-sm mb-2' : 'text-sm'
      }`}>
        {label}
        {required && (
          <span className="text-red-500 ml-1">*</span>
        )}
      </label>
      <div className={`relative rounded-md shadow-sm ${
        isMobile ? '' : 'mt-1'
      }`}>
        <div className={`absolute inset-y-0 left-0 flex items-center pointer-events-none ${
          isMobile ? 'pl-3' : 'pl-3'
        }`}>
          <Icon className={`text-gray-400 ${
            isMobile ? 'h-4 w-4' : 'h-5 w-5'
          }`} />
        </div>
        <input
          type={type}
          name={name}
          id={name}
          value={value || ''}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          className={`block w-full pr-3 rounded-md ${
            isMobile ? 'pl-10 py-3 text-base' : 'pl-10 py-2 sm:text-sm'
          } ${
            disabled 
              ? 'bg-gray-50 text-gray-500 border-gray-200' 
              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
          } ${error ? 'border-red-300' : ''}`}
        />
      </div>
      {error && (
        <p className={`mt-2 text-red-600 ${
          isMobile ? 'text-sm' : 'text-sm'
        }`}>{error}</p>
      )}
    </div>
  );
};

export default Settings;